import flatten from 'lodash/flatten';
import merge from 'lodash/merge';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useHasMounted} from '../../../common/shared/hooks/useHasMounted';
import {usePrevious} from '../../../common/shared/hooks/usePrevious';
import {twMerge} from '../../../common/twMerge';
import {FontLoadStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/FontLoadStatus.enum';
import {TalkShapeType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkShapeType.enum';
import {TalkType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkType.enum';
import {TalkShape} from '../TalkShape/TalkShape';
import {TalkTextProps} from './TalkTextProps.interface';

export function TalkText({
  text,
  textAlign,
  talkShapeType,
  talkType,
  isShowingTalk,
  fontLoadStatus,
  talkColor,
  talkBorderColor,
  textColor,
  characterName,
  characterNameColor,
  talkPosition,
  hasTalkTail,
  style,
  ...props
}: TalkTextProps) {
  const [parsedText, setParsedText] = useState('');
  const tempTextRef = useRef<HTMLDivElement>(null);
  const [isLineBreaked, setIsLineBreaked] = useState(false);
  const hasMounted = useHasMounted();
  const prevText = usePrevious(text);

  useEffect(() => {
    if (text !== prevText) {
      setParsedText('');
    }
  }, [prevText, text]);

  // 매끄럽게 단어 단위로 줄바꿈을 해 주기 위해 text를 parsing한다.
  // 파싱되는 과정을 유저에게 보여주지 않기 위해 useLayoutEffect를 사용해서 Dom update 전에 처리
  useLayoutEffect(() => {
    // text(대사) 가 없거나 이미 텍스트 parsing을 한 경우는 별도의 처리가 더 필요 없으므로 early return한다.
    if (!text || parsedText) {
      return;
    }
    // 톡이 화면에 없는 상태라면 영역도 없으므로 제대로 된 줄바꿈 판단을 하지 못해 return한다
    if (!isShowingTalk) {
      return;
    }
    // 폰트가 아직 로딩 전인 상태면 제대로 줄바꿈이 되지 않으므로 return 한다.
    if (fontLoadStatus === FontLoadStatus.BeforeLoad) {
      return;
    }
    function processParsingtext() {
      if (!tempTextRef.current) {
        return;
      }
      const tempTextEl = tempTextRef.current;
      // 기본 톡 높이.
      const initialHeight = tempTextEl.clientHeight;

      // 개행문자로 나눈 문장. 개행문자가 없으면 ["대사"] 처럼 아이템 1개짜리 배열이 된다.
      const splitedTextsByLineBreak = String(text).split('\n');

      // 단어들을 실제로 화면에 보여지는 한 줄 단위 문장 배열로 변환시켜준다. [문장, 문장, 문장] 형태
      function parsingWords(words: string[]) {
        return words.reduce((acc, word) => {
          if (acc.length === 0) {
            return [word];
          }
          const lastWord = acc[acc.length - 1];
          const mergedWord = `${lastWord} ${word}`;
          tempTextEl.innerHTML = mergedWord;
          // 초기값과 높이가 같으면 줄바꿈이 일어나지 않은 상황이므로 이전 문장에 해당 단어를 포함시킨다.
          if (initialHeight === tempTextEl.clientHeight) {
            acc.pop();
            return [...acc, mergedWord];
          }
          // 줄바꿈이 일어났으면 해당 단어를 새로운 문장으로 배열에 추가한다.
          else {
            return [...acc, word];
          }
        }, [] as string[]);
      }

      // 개행문자로 나누어진 문장을 파싱한 결과물 [[문장, 문장], [문장, 문장]] 형태의 nest 된 배열
      const parsedSplitedTexts = splitedTextsByLineBreak.map(line => {
        // 공백 기준으로 나눈 단어들
        const splitedWordsBySpace = line.split(' ');
        return parsingWords(splitedWordsBySpace);
      });

      // nest된 배열을 flatten 해 준다.
      const flattedTexts = flatten(parsedSplitedTexts);

      // 각 배열의 아이템은 한 줄 단위의 텍스트이므로 개행문자로 연결시켜준다.
      const resultText = flattedTexts.join('\n');
      setParsedText(resultText);

      tempTextEl.innerHTML = resultText;
      // 기본 톡 높이보다 최종 톡 높이가 높아졌으면 lineBreak가 일어난 톡이다.
      setIsLineBreaked(initialHeight < tempTextEl.clientHeight);
      tempTextEl.innerHTML = ' ';
    }
    if (fontLoadStatus === FontLoadStatus.Success) {
      processParsingtext();
    } else if (fontLoadStatus === FontLoadStatus.Fail) {
      // 웹폰트를 뷰어 전역에 적용 해 두었기 때문에, 한번 마운트 된 이후에는 폰트가 로딩된 이후이다.
      if (hasMounted) {
        processParsingtext();
      }
      // Fail이 떨어지고 첫 렌더라면 1초뒤에 줄바꿈을 적용한다
      else {
        setTimeout(processParsingtext, 1000);
      }
    }
  }, [text, parsedText, isShowingTalk, fontLoadStatus, hasMounted, prevText]);
  return (
    <TalkShape
      className={twMerge(
        'relative my-0 inline-block text-28tvs',
        talkShapeType === TalkShapeType.Bubble &&
          BUBBLE_TALK_TYPE_STYLE[talkType]
      )}
      style={merge(
        {
          color: textColor,
          textAlign:
            isLineBreaked || talkShapeType === TalkShapeType.Box
              ? textAlign
              : 'center',
        },
        style
      )}
      talkShapeType={talkShapeType}
      characterName={characterName}
      characterNameColor={characterNameColor}
      talkPosition={talkPosition}
      talkColor={talkColor}
      talkBorderColor={talkBorderColor}
      hasTalkTail={hasTalkTail}
      {...props}
    >
      <div className="relative whitespace-pre-wrap break-all leading-[1.36] tracking-[-0.5px]">
        {parsedText || text}
        {/* 실제로 줄바꿈이 일어나는 지점을 찾기 위해 임시로 사용하는 element. parsing이 끝나면 노출하지 않는다. script(대사)가 없는 경우에도 처리할 필요가 없으니 노출하지 않는다*/}
        {text && (!parsedText || text !== prevText) && (
          <div
            ref={tempTextRef}
            className="absolute max-w-full whitespace-pre-wrap break-all text-28tvs leading-[1.36] tracking-[-0.5px] opacity-0"
          >
            {' '}
          </div>
        )}
      </div>
    </TalkShape>
  );
}

const BUBBLE_TALK_TYPE_STYLE: {[key in TalkType]: string} = {
  [TalkType.Narration]: 'max-w-660tvs font-medium',
  [TalkType.Omniscient]:
    'max-w-567tvs min-w-110tvs px-29tvs py-22tvs rounded-40tvs',
  [TalkType.Talker]:
    'rounded-26tvs max-w-474tvs min-w-78tvs px-26tvs pt-19.5tvs pb-20.5tvs',
  [TalkType.None]: '',
};
